import React from 'react';
import { GetServerSidePropsContext, NextPage } from 'next';
import { NextSeo, NextSeoProps } from 'next-seo';
import { axiosClient } from 'src/lib/api/axios';
import { dehydrate, QueryClient } from '@tanstack/react-query';
import { getProjectConfigByHost } from 'app.core/common/module/getProjectConfigByHost';
import { convertExamDetailQueryString } from 'app.feature/academy/cbt.detail/module/convertExamDetailQueryString';
import ScreenCbtDetail from 'src/app.feature/academy/cbt.detail/screen/ScreenCbtDetail';
import ExamApi from 'lib/api/exam';
import UserApi from 'lib/api/users';
import { TExamsInfo } from 'app.type/examsInfo';
import { TUserLikesExams } from 'app.type/userLikesExams';
import {TExams} from "app.type/exams";

type TProps = {
  seoData: NextSeoProps;
};

const CbtDetailPage: NextPage<TProps> = ({ seoData }) => {
  return (
    <>
      {seoData && <NextSeo {...seoData} />}
      <ScreenCbtDetail />
    </>
  );
};

export default CbtDetailPage;

export async function getServerSideProps(context: GetServerSidePropsContext) {
  const queryClient = new QueryClient();
  const { examIdx } = context.query;
  const currentHost = context?.req?.headers.host ?? '';

  if (!examIdx) {
    return {
      notFound: true,
    };
  }

  const results = await Promise.allSettled([
    // 시험 조회수 증가
    // TODO: use useQuery
    axiosClient.post(`/apiV2/exams/${examIdx}/view`),

    // 시험정보조회
    queryClient.prefetchQuery<TExamsInfo>([`/apiV2/exams/${examIdx}`], () =>
      ExamApi.getExamInfo(Number(examIdx))
    ),
    // 시험회차조회
    queryClient.prefetchQuery(
      [`/apiV2/exams/${examIdx}/exam-details`, convertExamDetailQueryString(context.query)],
      () => ExamApi.getExamDetails(Number(examIdx), convertExamDetailQueryString(context.query))
    ),

    // 사용자가 좋아요한 시험목록
    queryClient.prefetchQuery<TUserLikesExams>(['/apiV2/user/me/likes/exams'], () =>
      UserApi.getUserLikes()
    ),

    // 사용자가 구매한 시험 목록
    queryClient.prefetchQuery<TExams[]>(['/apiV2/user/me/exams'], () =>
      UserApi.getUserPurchases()
    ),

  ]);

  // 실패한 응답 처리
  results.forEach((result, index) => {
    if (result.status === 'rejected') {
      console.error(`#err-web-server (request ${index}): `, result.reason.toString());
    }
  });

  const dehydratedState = dehydrate(queryClient);

  const { examDetailSeo } = getProjectConfigByHost(currentHost);

  const examInfo = queryClient.getQueryData<TExamsInfo>([`/apiV2/exams/${examIdx}`]);

  const seoData = {
    ...(examDetailSeo && examDetailSeo(examInfo as TExamsInfo)),
  };

  return { props: { dehydratedState, seoData } };
}
